import {writable, get, derived} from 'svelte/store';
import {EntityTypes} from 'Store/investorProfile/profilesStore';
import api from 'Utility/api';
import {ApiTarget} from 'Types/api';
import type {ValidationError} from 'Utility/validations/types';
import {loading} from 'Store/investorProfile/loadingStore';
import {dealId} from 'Store/investorProfile/dealIdStore';
import {investorId} from 'Store/investorProfile/investorIdStore';
import {
  buildPayload,
  defaultEntity,
  buildBody,
} from 'Store/investorProfile/entityStore';
import type {InvestorProfileErrors} from 'Store/investorProfile/errorsStore';
import type {Entity} from 'Store/investorProfile/entityStore';
import {errors} from 'Store/investorProfile/errorsStore';
import {alertStore} from 'Store/LocalAlertStore';
import validationErrors from 'Store/FormValidationStore';
import {buildValidationErrorsFromResponse} from 'Utility/checkoutFlow/contactInformation/buildValidationErrorsFromResponse';
import {investor} from 'Store/ibex/checkouts/investorCheckoutStore';
import {
  raiseCheckoutEvent,
  CheckoutEventAction,
} from 'Utility/ibex/checkouts/checkoutEvents';
import type {Investor} from 'Utility/investorFlow/entity';

export const processError = (error: string, profileId: number): void => {
  if (error) {
    alertStore.setAlert({
      type: 'danger',
      message: error,
    });
    const preventOCIMessage =
      'To continue your investments with your Canadian profile, please follow the link below.';
    if (error.includes(preventOCIMessage)) {
      unsupportedProfileId.set(profileId);
      invalidProfileId.set(null);
    } else {
      invalidProfileId.set(profileId);
      unsupportedProfileId.set(null);
    }
  }
};

export const clearErrorState = (): void => {
  alertStore.clear();
  duplicateProfileDetected.set(false);
  unsupportedProfileId.set(null);
  invalidProfileId.set(null);
};

const createSelectedProfileIdStore = () => {
  const selectedProfileId = writable<number>(undefined);
  const {set, subscribe} = selectedProfileId;

  return {
    set,
    subscribe,
    updateProfileId: async (
      profileId: number,
      checkoutStep: string = null
    ): Promise<void> => {
      clearErrorState();

      const updateParams = checkoutStep
        ? {
            investor_profile_id: profileId,
            current_step: checkoutStep,
          }
        : {
            investor_profile_id: profileId,
          };

      const response: Response = await api.patch(
        ApiTarget.V1,
        `deals/${get(dealId)}/investors/${get(investorId)}`,
        {
          body: JSON.stringify(updateParams),
          headers: {'Content-Type': 'application/json'},
        }
      );

      const json = await response.json();
      set(profileId);
      if (response?.ok) {
        investor.update(investor => {
          return {
            ...investor,
            checkout_state: json.checkout_state,
            investor_profile_id: profileId,
          };
        });
      } else {
        const error = json?.error;
        loading.set(false);
        if (json?.investment_limit || json?.below_minimum) throw json;
        processError(error, profileId);
      }
    },
    getProfileId: async (): Promise<void> => {
      const body = (await api.getJson(
        ApiTarget.V1,
        `deals/${get(dealId)}/investors/${get(investorId)}`
      )) as Investor;
      // this API contains the investor profile + user data data
      const id = body.investor_profile_id;

      if (id) {
        set(id);
        await entity.getProfile(id);
      }
    },
  };
};

export const ProfileEndpoint = Object.freeze({
  [EntityTypes.INDIVIDUAL]: 'investor_profiles/individuals',
  [EntityTypes.JOINT]: 'investor_profiles/joints',
  [EntityTypes.CORPORATION]: 'investor_profiles/corporations',
  [EntityTypes.TRUST]: 'investor_profiles/trusts',
  [EntityTypes.MANAGED]: 'investor_profiles/managed',
});

type CreateProfileOptions = {
  attachProfileToCurrentInvestor: boolean;
};

const entityStore = () => {
  const entity = writable<Entity>(defaultEntity());
  const {set, subscribe, update} = entity;

  const createProfile = async (
    options: CreateProfileOptions = {attachProfileToCurrentInvestor: true}
  ): Promise<boolean> => {
    loading.set(true);
    const profile: Entity = get(entity);
    const profileId = get(selectedProfileId);
    let response: Response;
    const params: string = JSON.stringify(buildPayload(profile));
    const payload = {
      body: params,
      headers: {'Content-Type': 'application/json'},
    };

    if (profileId) {
      response = await api.patch(
        ApiTarget.V1,
        `${ProfileEndpoint[profile.type]}/${profileId}`,
        payload
      );
    } else {
      response = await api.post(
        ApiTarget.V1,
        ProfileEndpoint[profile.type],
        payload
      );
    }

    if (response?.ok) {
      raiseCheckoutEvent(
        profileId
          ? CheckoutEventAction.PROFILE_UPDATED
          : CheckoutEventAction.PROFILE_CREATED,
        buildPayload(profile)
      );
      const body = await response.json();
      alertStore.clear();
      duplicateProfileDetected.set(false);
      unsupportedProfileId.set(null);

      if (options.attachProfileToCurrentInvestor) {
        await selectedProfileId.updateProfileId(body.id);
      } else {
        selectedProfileId.set(body.id);
      }

      loading.set(false);
      return true;
    } else {
      const json = await response.json();
      const e: InvestorProfileErrors = json['error'];
      errors.set(e);

      if (Object.keys(e).length) {
        const builtErrors: {
          [inputName: string]: ValidationError;
        } = buildValidationErrorsFromResponse(e);

        validationErrors.set(builtErrors);
      }

      if (json.error.profile) {
        alertStore.setAlert({
          type: 'danger',
          message:
            'Duplicate profile detected. Please select from an existing profile or modify your inputs.',
        });
        duplicateProfileDetected.set(true);
        unsupportedProfileId.set(null);
        invalidProfileId.set(null);
      }
    }

    loading.set(false);
    return false;
  };

  const getProfile = async (profileId: number): Promise<void> => {
    const {profile} = (await api.getJson(
      ApiTarget.V1,
      `investor_profiles/profile/${profileId}`
    )) as any;

    update(entity => {
      const newEntity = {...entity, ...buildBody(profile)};
      raiseCheckoutEvent(
        CheckoutEventAction.PROFILE_SELECTED,
        buildPayload(newEntity)
      );
      return newEntity;
    });
  };

  const createProfileAndUpdateStore = async (): Promise<boolean> => {
    const hasErrors = await createProfile();
    await getProfile(get(selectedProfileId));

    return hasErrors;
  };

  return {
    set,
    subscribe,
    update,
    createProfile,
    getProfile,
    createProfileAndUpdateStore,
  };
};

export const entity = entityStore();
export const selectedProfileId = createSelectedProfileIdStore();
export const unsupportedProfileId = writable<number>(null);
// Profile id for which backend threw error
export const invalidProfileId = writable<number>(null);
export const duplicateProfileDetected = writable<boolean>(false, set => {
  const setFalse = () => {
    set(false);
  };

  const {
    jointHolder,
    signingOfficer,
    beneficialOwners = writable([]),
    trustees = writable([]),
  } = get(entity);

  const unsubscribers = [
    entity.subscribe(setFalse),
    jointHolder?.subscribe(setFalse),
    signingOfficer?.subscribe(setFalse),
    beneficialOwners?.subscribe(setFalse),
    ...get(beneficialOwners)?.map(owners => owners?.subscribe(setFalse)),
    trustees?.subscribe(setFalse),
    ...get(trustees)?.map(trustee => trustee?.subscribe(setFalse)),
  ];

  return () =>
    unsubscribers
      .filter(v => v !== undefined)
      .forEach(unsubscriber => unsubscriber());
});
export const entityDisplayName = derived(entity, $entity => {
  if ($entity.name) return $entity.name;
  return `${$entity.firstName || ''} ${$entity.lastName || ''}`.trim();
});
